import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, MenuItem, FormControl, Select, Stack, useTheme, useMediaQuery } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import { useDebounce } from 'hooks/useDebounce';
import errorHelper from 'helpers/errorHelper';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import PageToolbar from '_lex/_atoms/PageToolbar';
import { QuickSearch } from 'components/_atoms';
import { RiskPoolSharingContext } from '../context';
import { AppContext } from 'views/App/context';
import useJurisdictions from 'data_sources/useJurisdictions';
import OrganizationTable from './components/OrganizationTable';

const RiskPoolSharingList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const { organizations, organizationsQuery } = useContext(RiskPoolSharingContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Filter state
  const [searchInput, setSearchInput] = useState('');
  const [jurisdictionInput, setJurisdictionInput] = useState<string>('');
  const [cityInput, setCityInput] = useState<string>('');

  // Debounced values for filtering (300ms delay)
  const debouncedSearch = useDebounce(searchInput, 300);
  const debouncedJurisdiction = useDebounce(jurisdictionInput, 300);
  const debouncedCity = useDebounce(cityInput, 300);


  // Fetch jurisdictions for filter dropdown
  const { data: jurisdictions } = useJurisdictions({}, (errors) => {
    errorHelper.enqueueErrors(errors);
  });

  // Create a map of state abbreviation to state name
  const stateMap = useMemo(() => {
    if (!jurisdictions) return {};
    return jurisdictions.reduce((acc, jurisdiction) => {
      acc[jurisdiction.abbreviation] = jurisdiction.name;
      return acc;
    }, {} as Record<string, string>);
  }, [jurisdictions]);

  // Extract unique jurisdiction abbreviations from organizations data
  const availableJurisdictions = useMemo(() => {
    if (!jurisdictions) return [];

    // Get unique state abbreviations from organizations
    const stateAbbreviations = organizations
      .map((org) => org.state)
      .filter((state): state is string => !!state);
    const uniqueStates = Array.from(new Set(stateAbbreviations));

    // Map to full names using jurisdictions endpoint
    return uniqueStates
      .map((abbr) => ({
        value: abbr,
        label: stateMap[abbr] || abbr,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [organizations, jurisdictions, stateMap]);

  const availableCities = useMemo(() => {
    const cities = organizations
      .map((org) => org.city)
      .filter((city): city is string => !!city);

    const uniqueCities = Array.from(new Set(cities)).sort();

    return uniqueCities.map((city) => ({
      value: city,
      label: city,
    }));
  }, [organizations]);

  // Filter organizations based on all filters
  const filteredOrganizations = useMemo(() => {
    let filtered = organizations;

    // Filter by search text (organization name)
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter((org) =>
        org.name?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by jurisdiction (state)
    if (debouncedJurisdiction) {
      filtered = filtered.filter((org) => org.state === debouncedJurisdiction);
    }

    // Filter by city
    if (debouncedCity) {
      filtered = filtered.filter((org) => org.city === debouncedCity);
    }

    return filtered;
  }, [organizations, debouncedSearch, debouncedJurisdiction, debouncedCity]);


  const canAdd = currentOrganizationId && hasPermission('addRelatedOrg', currentOrganizationId);

  return (
    <CommonLayout
      headerProps={{
        title: t('riskPoolSharing.title'),
        subtitle: t('riskPoolSharing.subtitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          sx={{ display: 'flex', alignItems: 'start' }}
          leftItems={
            <Stack
              direction={isMobile ? 'column' : 'row'}
              alignItems="center"
              sx={{
                width: isMobile ? '100%' : 'auto',
                flexWrap: 'wrap',
                alignContent: 'flex-start',
                gap: 2,
                maxWidth: '100%',
              }}
            >
              <QuickSearch
                id="risk_pool_search"
                initialValue={searchInput}
                onChange={(val) => setSearchInput(val)}
                placeholder={t('riskPoolSharing.filters.searchByOrganization')}
                minWidth={isMobile ? undefined : 180}
                fullWidth={isMobile}
                sx={{
                  flexGrow: isMobile ? 0 : 1,
                  maxWidth: isMobile ? '100%' : 220,
                  '& .MuiInputBase-root': {
                    height: 40,
                  },
                }}
              />
              <FormControl
                size="small"
                sx={{
                  minWidth: isMobile ? '100%' : 180,
                  flexGrow: isMobile ? 0 : 1,
                  maxWidth: isMobile ? '100%' : 220,
                  padding: 0
                }}
              >
                <Select
                  id="jurisdiction-filter"
                  value={jurisdictionInput}
                  onChange={(e) => setJurisdictionInput(e.target.value)}
                  displayEmpty
                  sx={{ height: 40 }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return `${t('riskPoolSharing.filters.searchByJurisdiction')}: All`;
                    }
                    return `${t('riskPoolSharing.filters.searchByJurisdiction')}: ${selected}`;
                  }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {availableJurisdictions.map((jurisdiction) => (
                    <MenuItem key={jurisdiction.value} value={jurisdiction.value}>
                      {jurisdiction.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{
                  minWidth: isMobile ? '100%' : 180,
                  flexGrow: isMobile ? 0 : 1,
                  maxWidth: isMobile ? '100%' : 220,
                  padding: 0

                }}
              >
                <Select
                  id="city-filter"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                  displayEmpty
                  sx={{ height: 40 }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return `${t('riskPoolSharing.filters.searchByCity')}: All`;
                    }
                    return `${t('riskPoolSharing.filters.searchByCity')}: ${selected}`;
                  }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {availableCities.map((city) => (
                    <MenuItem key={city.value} value={city.value}>
                      {city.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isMobile && canAdd && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/admin/lexid/risk-pool-sharing/add')}
                  sx={{ alignSelf: 'stretch', height: 40 }}
                >
                  {t('riskPoolSharing.actions.addOrganization')}
                </Button>
              )}
            </Stack>
          }
          rightItems={
            !isMobile && canAdd ? (
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/admin/lexid/risk-pool-sharing/add')}
                sx={{ height: 40, minWidth: 170 }}
              >
                {t('riskPoolSharing.actions.addOrganization')}
              </Button>
            ) : null
          }
        />
        <OrganizationTable
          organizations={organizations}
          filteredOrganizations={filteredOrganizations}
          organizationsQuery={organizationsQuery}
          stateMap={stateMap}
          hasPermission={(permission: string, orgId: string) => hasPermission(permission, orgId)}
          currentOrganizationId={currentOrganizationId}
        />
      </Box>
    </CommonLayout>
  );
};

export default RiskPoolSharingList;